import { CalendarOutlined, HomeOutlined, RedoOutlined } from '@ant-design/icons';
import noImagePreview from '@common/assets/no-image-preview.png';
import { THEME } from '@common/constants/theme.constant';
import { PrismicImage } from '@common/general-ui/image/image.component';
import { Item } from '@common/general-ui/summary/components/item.component';
import { Line } from '@common/general-ui/summary/components/line.component';
import { Tips } from '@common/general-ui/summary/components/tips.component';
import { StatusTagV2 } from '@common/general-ui/tag-v2/status-tag-v2.component';
import React from 'react';
import { css } from 'twin.macro';
import { ISummaryProps } from './summary.interface';
export type TStatuses = 'default' | 'error' | 'warning' | 'info' | 'success';

export const Summary = ({
    title,
    dataTestId,
    children,
    deliveryFee,
    oddShopDeliveryFee,
    discounts,
    discountAmount,
    total,
    address,
    frequency,
    arriving,
    className,
    boxInfo,
    showTip,
    deliveryTag,
    addons,
}: ISummaryProps): JSX.Element => {
    return (
        <div tw="flex flex-col gap-4 rounded-md" css={styles.root} className={className} data-testid={dataTestId}>
            {title && (
                <h3 tw="uppercase font-secondary text-2.5xl lg:text-xl lg:font-primary lg:normal-case hidden lg:block">
                    {title}
                </h3>
            )}

            <div tw="flex">
                <PrismicImage
                    tw="rounded object-contain"
                    css={styles.image}
                    src={boxInfo.imgUrl || noImagePreview}
                    alt={boxInfo.title || 'box-image'}
                    width={144}
                    height={144}
                />

                <div tw="flex flex-col ml-3 self-center">
                    <div tw="text-base">{boxInfo.title}</div>
                    <div
                        tw="text-sm font-light"
                        css={`
                            color: ${THEME.TEXT_LIGHT_GRAY};
                        `}
                    >
                        {boxInfo.size}
                    </div>
                </div>
            </div>

            {(arriving || address || frequency) && (
                <div tw="flex flex-col gap-2 text-sm font-light">
                    {arriving && arriving.date && arriving.date != 'Invalid Date' && (
                        <div tw="flex items-start gap-2">
                            <CalendarOutlined tw="pt-0.5" />
                            <div>
                                Arriving {arriving.date}
                                <span
                                    css={css`
                                        color: ${THEME.GRAY};
                                    `}
                                >
                                    {' '}
                                    ({arriving.time})
                                </span>
                            </div>
                        </div>
                    )}
                    {address && (
                        <div tw="flex items-start gap-2">
                            <HomeOutlined tw="pt-0.5" />
                            <div>{address}</div>
                        </div>
                    )}
                    {frequency && (
                        <div tw="flex items-start gap-2">
                            <RedoOutlined tw="pt-0.5" />
                            <span>{frequency} subscription</span>
                        </div>
                    )}
                </div>
            )}

            <div tw="flex flex-col gap-3">
                <Line />
                <Item title={boxInfo.title} price={boxInfo.price} />
                {addons?.map((addon) => {
                    return <Item key={addon.title} title={addon.title} quantity={addon.quantity} price={addon.price} />;
                })}

                <Line />
                <Item title={'Delivery Fee'} price={deliveryFee?.price} />
                {discounts?.map((discount) => {
                    if (discount.title) {
                        return (
                            <Item
                                key={discount.title}
                                title={discount.title.includes('Referral-coupon') ? 'Refer a friend' : discount.title}
                                price={discountAmount && -discountAmount}
                                css={css`
                                    color: ${THEME.LIGHT_GREEN};
                                `}
                            />
                        );
                    }
                    return null;
                })}
                {oddShopDeliveryFee && <Item title={'The Odd Shop Delivery'} price={oddShopDeliveryFee.price} />}
                {deliveryTag && (
                    <StatusTagV2 tw="w-full" name="delivery" status={deliveryTag.status}>
                        {deliveryTag.text}
                    </StatusTagV2>
                )}
                <Line />
                <Item title="Total" price={total} tw="font-normal" />
            </div>

            {children && <div tw="w-full">{children}</div>}

            {showTip && <Tips />}
        </div>
    );
};

const styles = {
    root: css`
        background-color: ${THEME.WHITE};
    `,
    image: css`
        width: 72px;
        height: 72px;
        background-color: ${THEME.BEIGE};
    `,
};
